import { ChangeDetectionStrategy, Component, DestroyRef, computed, effect, inject, signal, untracked } from '@angular/core';
import { DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { map } from 'rxjs/operators';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { RadioBoxComponent, RadioOption } from '@libs/front/components/radiobox/radiobox.component';
import { DataTableComponent, TableColumn } from '@libs/front/components/data-table/data-table.component';
import { ButtonListComponent } from '@libs/front/components/button-list/button-list.component';
import { ToleranceBarComponent } from 'src/app/shared/components/tolerance-bar/tolerance-bar.component';
import { UtilisateurService } from 'src/app/services/utilisateur.service';
import { SiteNavigationService } from 'src/app/services/new_services/site-navigation.service';
import { KeycloakUtilisateurService } from 'src/app/services/keycloak/keycloak-utilisateur.service';
import { CONFIG_SECTION_ROLES } from 'src/app/features/config/config-roles';
import { Hole, HoleLoad, HoleStatus, HoleStatusHistoryUtil } from 'src/app/services/hole.service';
import { BulkUnit, HoleToleranceThreshold, bulkUnitOf, getThreshold } from 'src/app/bo/site-hole-tolerance';

import { BlastHolesStateService, HoleCellOverlay } from '../../blast-holes-state.service';
import { HolesLegendPanelComponent } from '../../shared/holes-legend-panel/holes-legend-panel.component';
import { ProgressChipComponent, RingSegment } from '../../shared/progress-chip/progress-chip.component';
import { readColumnKeys, storeColumnKeys } from '../../shared/column-preferences';
import {
  DEFAULT_QUALITY_METRIC,
  POWDER_FACTOR_SCALE_COLORS,
  PowderFactorScale,
  QUALITY_BUCKETS,
  QUALITY_BUCKET_COLORS,
  QUALITY_BUCKET_LABEL_KEYS,
  QUALITY_METRICS,
  QUALITY_NO_DATA_COLOR,
  QualityBucket,
  QualityMetric,
  QualityMetricId,
  countByBucket,
  hasNegativeSide,
  holeVolume,
  isQualityMetricId,
  powderFactorColor,
  powderFactorScale,
  qualityBucket,
  qualityValue,
  thresholdFor,
} from '../../shared/hole-quality.utils';

interface QualityRow {
  publicGuid:       string;
  name:             string;
  status:           string;
  /** Bucket of the selected metric, translated (the value itself for the powder factor) */
  quality:          string;
  head:             string | null;
  toe:              string | null;
  lengthPlanned:    string | null;
  lengthDrilled:    string | null;
  lengthMined:      string | null;
  lengthDelta:      string | null;
  bulkPlanned:      string | null;
  bulkMeasured:     string | null;
  bulkDelta:        string | null;
  /** Rock volume of the hole's Voronoi cell (m³) */
  volume:           string | null;
  /** (measured, else planned bulk) / volume, kg/m³ */
  powderFactor:     string | null;
  stemmingPlanned:  string | null;
  stemmingMeasured: string | null;
  stemmingDelta:    string | null;
}

interface LegendMetric {
  metric:    QualityMetric;
  option:    RadioOption[];
  threshold: HoleToleranceThreshold | undefined;
  unit:      string;
}

interface BucketCount {
  bucket: QualityBucket | 'noData';
  color:  string;
  label:  string;
  count:  number;
}

/** One ring of the panel head: the bucket split of a metric that at least one hole has a value for. */
interface MetricRing {
  metricId: QualityMetricId;
  segments: RingSegment[];
  /** Short metric name, shown next to the ring. */
  label:    string;
  /** Full metric name and per-bucket counts. */
  title:    string;
}

const COLUMNS: readonly TableColumn[] = [
  { key: 'name',             label: 'QAQC_TAB.HOLE' },
  { key: 'status',           label: 'QAQC_TAB.STATUS' },
  { key: 'quality',          label: 'HOLES_TAB.QUALITY_COL_QUALITY' },
  { key: 'head',             label: 'HOLES_TAB.QUALITY_COL_HEAD' },
  { key: 'toe',              label: 'HOLES_TAB.QUALITY_COL_TOE' },
  { key: 'lengthPlanned',    label: 'QAQC_TAB.LENGTH_PLANNED' },
  { key: 'lengthDrilled',    label: 'QAQC_TAB.LENGTH_DRILLED' },
  { key: 'lengthMined',      label: 'QAQC_TAB.MINED_DRILLED' },
  { key: 'lengthDelta',      label: 'HOLES_TAB.QUALITY_COL_LENGTH' },
  { key: 'bulkPlanned',      label: 'HOLES_TAB.QUALITY_COL_BULK_PLANNED' },
  { key: 'bulkMeasured',     label: 'HOLES_TAB.QUALITY_COL_BULK_MEASURED' },
  { key: 'bulkDelta',        label: 'HOLES_TAB.QUALITY_COL_BULK_DELTA' },
  { key: 'volume',           label: 'HOLES_TAB.QUALITY_COL_VOLUME' },
  { key: 'powderFactor',     label: 'HOLES_TAB.QUALITY_COL_POWDER_FACTOR' },
  { key: 'stemmingPlanned',  label: 'HOLES_TAB.QUALITY_COL_STEMMING_PLANNED' },
  { key: 'stemmingMeasured', label: 'HOLES_TAB.QUALITY_COL_STEMMING_MEASURED' },
  { key: 'stemmingDelta',    label: 'HOLES_TAB.QUALITY_COL_STEMMING_DELTA' },
];

/** Columns shown by default; the picker can add the others. */
const DEFAULT_COLUMN_KEYS = [
  'name', 'quality', 'head', 'toe', 'lengthPlanned', 'lengthDrilled', 'lengthMined', 'lengthDelta', 'bulkDelta', 'stemmingDelta',
];

/** Columns whose values come from the blast loading query. */
const LOADING_COLUMN_KEYS: ReadonlySet<string> = new Set([
  'bulkPlanned', 'bulkMeasured', 'bulkDelta', 'powderFactor',
  'stemmingPlanned', 'stemmingMeasured', 'stemmingDelta',
]);

/**
 * Holes › Quality view.
 * Legend: one radio per metric (collar / toe position / hole length / bulk / powder factor /
 * stemming final); the selected one shows the site tolerance bar and the bucket counts. The map
 * colours each hole by its bucket (green / yellow / red, grey without data). Thresholds come from
 * the site hole tolerance; bulk, powder factor and stemming need the blast loading query, loaded
 * on demand. The powder factor divides the bulk explosive (measured, else planned) by the rock
 * volume of the hole: its Voronoi cell area (shared with the Geology map cells) × its best known
 * length. It has no site tolerance: holes and cells are coloured on a blue → green → red scale
 * spanning the blast's own values (under loaded → over loaded).
 * Above the table, one small ring chip per metric holding values shows its as designed / in tolerance /
 * out of spec split next to the metric's short name; clicking one selects that metric.
 * Design source : Figma 2544:685487
 */
@Component({
  selector:        'app-holes-quality-view',
  standalone:      true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers:       [DecimalPipe],
  imports: [
    DecimalPipe,
    FormsModule,
    TranslateModule,
    RadioBoxComponent,
    DataTableComponent,
    ButtonListComponent,
    ToleranceBarComponent,
    HolesLegendPanelComponent,
    ProgressChipComponent,
  ],
  templateUrl: './holes-quality-view.component.html',
  styleUrls:   ['./holes-quality-view.component.scss'],
})
export class HolesQualityViewComponent {
  protected readonly state = inject(BlastHolesStateService);
  private readonly userService = inject(UtilisateurService);
  private readonly siteNav     = inject(SiteNavigationService);
  private readonly translate   = inject(TranslateService);
  private readonly decimal     = inject(DecimalPipe);
  private readonly router      = inject(Router);
  private readonly keycloak    = inject(KeycloakUtilisateurService);

  /** The tolerance settings are in the site configuration, reserved to its roles. */
  protected readonly canConfigureSite = this.keycloak.hasAnyRole([...CONFIG_SECTION_ROLES.sites]);

  private readonly lang = toSignal(
    this.translate.onLangChange.pipe(map(event => event.lang)),
    { initialValue: this.translate.currentLang },
  );

  // ── Legend ────────────────────────────────────────────────────────────────

  protected readonly metricId = signal<QualityMetricId>(DEFAULT_QUALITY_METRIC);

  /** Unit the site chose for its bulk threshold: the bulk deviation is computed and shown in it. */
  protected readonly bulkUnit = computed<BulkUnit>(() => bulkUnitOf(getThreshold(this.state.tolerance(), 'BULK')));

  protected readonly legendMetrics = computed<LegendMetric[]>(() => {
    this.lang();
    const tolerance = this.state.tolerance();
    return QUALITY_METRICS.map(metric => {
      const threshold = thresholdFor(tolerance, metric.id);
      const unit = metric.id === 'BULK' ? this.bulkUnit() : threshold?.unit || metric.defaultUnit;
      return {
        metric,
        threshold,
        unit,
        option: [{ value: metric.id, label: `${this.translate.instant(metric.labelKey)} (${unit})` }],
      };
    });
  });

  protected readonly selectedMetric = computed(() =>
    QUALITY_METRICS.find(metric => metric.id === this.metricId()) ?? QUALITY_METRICS[0],
  );

  protected readonly selectedThreshold = computed(() => thresholdFor(this.state.tolerance(), this.metricId()));

  protected readonly hasNegativeSide = hasNegativeSide;

  protected readonly isPowderFactor = computed(() => this.metricId() === 'POWDER_FACTOR');

  protected readonly scaleColors = POWDER_FACTOR_SCALE_COLORS;

  /** Whether the selected metric needs loading data that is not available. */
  protected readonly needsLoadingData = computed(() =>
    this.selectedMetric().source === 'loading' && !this.state.loading(),
  );

  // ── Buckets ───────────────────────────────────────────────────────────────

  /** publicGuid → bucket for the selected metric (null = no value or no threshold). */
  private readonly buckets = computed<ReadonlyMap<string, QualityBucket | null>>(() => {
    const metric = this.metricId();
    const threshold = this.selectedThreshold();
    this.state.loading();
    const cells = this.state.holeCells();
    const bulkUnit = this.bulkUnit();
    const result = new Map<string, QualityBucket | null>();
    for (const hole of this.state.holes()) {
      const volume = holeVolume(hole, cells.get(hole.publicGuid)?.area);
      result.set(hole.publicGuid, qualityBucket(qualityValue(metric, hole, this.state.loadFor(hole), volume, bulkUnit), threshold));
    }
    return result;
  });

  protected readonly bucketCounts = computed<BucketCount[]>(() => {
    this.lang();
    const noData = {
      bucket: 'noData' as const,
      color:  QUALITY_NO_DATA_COLOR,
      label:  this.translate.instant('HOLES_TAB.QUALITY_NO_DATA'),
    };
    if (this.isPowderFactor()) {
      // No buckets on the powder factor scale: only report the holes without a value
      let count = 0;
      for (const value of this.powderFactors().values()) if (value == null) count++;
      return [{ ...noData, count }];
    }
    const counts = countByBucket(this.buckets().values());
    const rows: BucketCount[] = QUALITY_BUCKETS.map(bucket => ({
      bucket,
      color: QUALITY_BUCKET_COLORS[bucket],
      label: this.translate.instant(QUALITY_BUCKET_LABEL_KEYS[bucket]),
      count: counts[bucket],
    }));
    rows.push({ ...noData, count: counts.noData });
    return rows;
  });

  /**
   * One ring chip per metric, above the table: the same three stats as the legend (as designed /
   * in tolerance / out of spec) over the holes that have a value, labelled with the metric's
   * short name. A metric no hole has a value for — no threshold, or loading data not fetched
   * yet — gets no ring.
   * POWDER_FACTOR is left out: it is an absolute value on a continuous scale, without buckets.
   */
  protected readonly metricRings = computed<MetricRing[]>(() => {
    this.lang();
    const tolerance = this.state.tolerance();
    this.state.loading();
    const holes = this.state.holes();
    const bulkUnit = this.bulkUnit();

    const rings: MetricRing[] = [];
    for (const metric of QUALITY_METRICS) {
      if (metric.id === 'POWDER_FACTOR') continue;

      const threshold = thresholdFor(tolerance, metric.id);
      if (!threshold) continue;

      const counts = countByBucket(
        holes.map(hole => qualityBucket(qualityValue(metric.id, hole, this.state.loadFor(hole), null, bulkUnit), threshold)),
      );
      const withValue = QUALITY_BUCKETS.reduce((sum, bucket) => sum + counts[bucket], 0);
      if (!withValue) continue;

      rings.push({
        metricId: metric.id,
        segments: QUALITY_BUCKETS.map(bucket => ({ color: QUALITY_BUCKET_COLORS[bucket], value: counts[bucket] })),
        label:    this.translate.instant(metric.shortKey),
        title:    [
          this.translate.instant(metric.labelKey),
          QUALITY_BUCKETS
            .map(bucket => `${this.translate.instant(QUALITY_BUCKET_LABEL_KEYS[bucket])} ${counts[bucket]}`)
            .join(', '),
        ].join(' — '),
      });
    }
    return rings;
  });

  // ── Powder factor scale ───────────────────────────────────────────────────

  /** publicGuid → powder factor (kg/m³) of every hole, null without loading data / volume. */
  private readonly powderFactors = computed<ReadonlyMap<string, number | null>>(() => {
    this.state.loading();
    const cells = this.state.holeCells();
    const result = new Map<string, number | null>();
    for (const hole of this.state.holes()) {
      const volume = holeVolume(hole, cells.get(hole.publicGuid)?.area);
      result.set(hole.publicGuid, qualityValue('POWDER_FACTOR', hole, this.state.loadFor(hole), volume));
    }
    return result;
  });

  /** Blue → green → red scale spanning the blast's powder factors; null when no hole has one. */
  protected readonly powderFactorScale = computed<PowderFactorScale | null>(() => powderFactorScale(this.powderFactors().values()));

  /** Marker colours pushed to the map: bucket colours, or the powder factor scale. */
  private readonly holeColors = computed<ReadonlyMap<string, string | null>>(() => {
    const colors = new Map<string, string | null>();
    if (this.isPowderFactor()) {
      const scale = this.powderFactorScale();
      for (const [holeId, value] of this.powderFactors()) colors.set(holeId, powderFactorColor(value, scale));
      return colors;
    }
    for (const [holeId, bucket] of this.buckets()) {
      colors.set(holeId, bucket ? QUALITY_BUCKET_COLORS[bucket] : null);
    }
    return colors;
  });

  /**
   * Voronoi cells drawn under the holes for the powder factor only: the metric is computed on
   * the cell (area × length), so the cells show which rock volume each hole is charged for.
   */
  private readonly cellOverlay = computed<HoleCellOverlay | null>(() =>
    this.isPowderFactor() ? { fills: this.holeColors() } : null,
  );

  // ── Table ─────────────────────────────────────────────────────────────────

  /** The bulk deviation column is labelled with the unit the site chose (% or kg). */
  protected readonly allColumns = computed<TableColumn[]>(() => {
    const bulkDeltaLabel = this.bulkUnit() === 'kg' ? 'HOLES_TAB.QUALITY_COL_BULK_DELTA_KG' : 'HOLES_TAB.QUALITY_COL_BULK_DELTA';
    return COLUMNS.map(column => column.key === 'bulkDelta' ? { ...column, label: bulkDeltaLabel } : column);
  });
  /** User's column choice, remembered in the browser (see shared/column-preferences.ts) */
  protected readonly visibleColumnKeys = signal<Set<string>>(readColumnKeys('quality', COLUMNS, DEFAULT_COLUMN_KEYS));
  protected readonly visibleColumns = computed(() =>
    this.allColumns().filter(column => this.visibleColumnKeys().has(column.key)),
  );

  protected onColumnsChange(keys: Set<string>): void {
    this.visibleColumnKeys.set(keys);
    storeColumnKeys('quality', keys);
  }

  protected readonly rows = computed<QualityRow[]>(() => {
    this.lang();
    const buckets = this.buckets();
    const cells   = this.state.holeCells();
    const isPowderFactor = this.isPowderFactor();
    return this.state.holes().map(hole =>
      this.toRow(hole, this.state.loadFor(hole), buckets.get(hole.publicGuid) ?? null, holeVolume(hole, cells.get(hole.publicGuid)?.area), isPowderFactor),
    );
  });

  constructor() {
    const destroyRef = inject(DestroyRef);

    // Site tolerances (thresholds)
    effect(() => {
      const siteId = this.userService.currentSite()?.siteId;
      if (siteId) untracked(() => void this.state.loadTolerance(siteId));
    });

    // Loading quantities (bulk / stemming), fetched once something displayed needs them:
    // the selected metric or a visible loading column
    effect(() => {
      const needed = this.selectedMetric().source === 'loading'
        || this.visibleColumns().some(column => LOADING_COLUMN_KEYS.has(column.key));
      if (this.state.blastId() && needed && !this.state.loading()) untracked(() => void this.state.loadLoading());
    });

    // Colour the map markers; restore the status icons when the view is left
    effect(() => this.state.holeColors.set(this.holeColors()));
    destroyRef.onDestroy(() => this.state.holeColors.set(null));

    // Powder factor: draw the Voronoi cells the volume is based on; clear them otherwise / when the view is left
    effect(() => this.state.cellOverlay.set(this.cellOverlay()));
    destroyRef.onDestroy(() => this.state.cellOverlay.set(null));
  }

  // ── Handlers ──────────────────────────────────────────────────────────────

  protected onMetricChange(value: string): void {
    if (isQualityMetricId(value)) this.metricId.set(value);
  }

  protected onRowClick(row: QualityRow): void {
    this.state.toggleHole(row.publicGuid);
  }

  protected retryLoading(): void {
    void this.state.loadLoading(true);
  }

  protected retryTolerance(): void {
    const siteId = this.userService.currentSite()?.siteId;
    if (siteId) void this.state.loadTolerance(siteId, true);
  }

  /** Opens the site hole tolerance page of the configuration, in the site's organization. */
  protected openToleranceSettings(): void {
    const siteId = this.userService.currentSite()?.siteId;
    if (!siteId) return;
    const orgId = this.siteNav.orgId();
    void this.router.navigate(['/new_config', 'sites', siteId, 'holes-tolerance'], {
      queryParams: orgId ? { orgId } : {},
    });
  }

  // ── Internals ─────────────────────────────────────────────────────────────

  private toRow(hole: Hole, load: HoleLoad | undefined, bucket: QualityBucket | null, volume: number | null, isPowderFactor: boolean): QualityRow {
    const bulkDelta     = qualityValue('BULK', hole, load, null, this.bulkUnit());
    const stemmingDelta = qualityValue('STEMMING', hole, load);
    const powderFactor  = qualityValue('POWDER_FACTOR', hole, load, volume);
    // The powder factor has no buckets: the "quality" cell carries the value itself
    const quality = isPowderFactor
      ? this.format(powderFactor) ?? this.translate.instant('HOLES_TAB.QUALITY_NO_DATA')
      : this.translate.instant(bucket ? QUALITY_BUCKET_LABEL_KEYS[bucket] : 'HOLES_TAB.QUALITY_NO_DATA');
    const geometry      = hole.Geometry;
    // Same semantics as the State view: drilled = drill length (else measured), mined = measured length.
    const lengthDrilled = geometry?.drilllength ?? geometry?.Measure?.length;
    const lengthMined   = geometry?.Measure?.length;
    return {
      publicGuid:       hole.publicGuid,
      name:             hole.name,
      status:           this.statusHistoryLabel(hole),
      quality,
      head:             this.format(qualityValue('HEAD', hole, load)),
      toe:              this.format(qualityValue('TOE_POSITION', hole, load)),
      lengthPlanned:    this.format(geometry?.Design?.length),
      lengthDrilled:    this.format(lengthDrilled),
      lengthMined:      lengthMined ? this.format(lengthMined) : null,
      lengthDelta:      this.format(qualityValue('LENGTH', hole, load), true),
      bulkPlanned:      this.format(load?.plannedTotalBulk),
      bulkMeasured:     this.format(load?.measuredTotalBulk),
      bulkDelta:        this.format(bulkDelta, true),
      volume:           this.format(volume),
      powderFactor:     this.format(powderFactor),
      stemmingPlanned:  this.format(load?.plannedSteaming),
      stemmingMeasured: this.format(load?.measuredSteaming),
      stemmingDelta:    this.format(stemmingDelta, true),
    };
  }

  private statusHistoryLabel(hole: Hole): string {
    const statuses = hole.statusHistory !== undefined && hole.statusHistory !== null
      ? HoleStatusHistoryUtil.getStatusHistory(hole.statusHistory)
      : [hole.Status as HoleStatus];
    return statuses.map(status => this.translate.instant('HOLE.' + status)).join(', ');
  }

  private format(value: number | null | undefined, signed = false): string | null {
    if (value == null || Number.isNaN(value)) return null;
    const text = this.decimal.transform(value, '1.2-2');
    return signed && value > 0 && text ? `+${text}` : text;
  }
}
